/**
 * Event management API adapter for eos-app (v2 with org support)
 *
 * Reason: Organization-scoped event management including member assignments
 * and access control for viewers and managers
 */

import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import ApiV2 from './api-v2';

export const LIST_EVENTS_CACHE_KEY = 'list-events';
export const LIST_EVENT_MEMBERS_CACHE_KEY = 'list-event-members';

export interface Event {
  id: string;
  orgId: string;
  name: string;
  description?: string;
  brandKit?: any;
  startDate?: string;
  endDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Note: brandKit is typed as any for backward compatibility in UI

export interface CreateEventRequest {
  name: string;
  description?: string;
  brandKit?: any;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface UpdateEventRequest {
  name?: string;
  description?: string;
  brandKit?: any;
  startDate?: string;
  endDate?: string;
  isActive?: boolean;
}

export interface EventMember {
  id: string;
  eventId: string;
  email: string;
  orgId: string;
  role: 'manager' | 'viewer';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AssignMemberRequest {
  email: string;
  role: 'manager' | 'viewer';
  isActive?: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Lists all events in the organization
 *
 * @reason Allows users to view all events they have access to
 */
export function useListEvents(orgId?: string): UseQueryResult<Event[]> {
  return useQuery(
    [LIST_EVENTS_CACHE_KEY, orgId],
    async () => {
      if (!orgId) {
        return [];
      }
      const response = await ApiV2.get<ApiResponse<Event[]>>(`/api/orgs/${orgId}/events`);
      return response.data || [];
    },
    {
      enabled: !!orgId,
    },
  );
}

/**
 * Gets a specific event by ID
 *
 * @reason Retrieves detailed event information
 */
export function useGetEvent(eventId?: string): UseQueryResult<Event | null> {
  return useQuery(
    ['event', eventId],
    async () => {
      if (!eventId) {
        return null;
      }
      const response = await ApiV2.get<ApiResponse<Event>>(`/api/events/${eventId}`);
      return response.data || null;
    },
    {
      enabled: !!eventId,
    },
  );
}

/**
 * Creates a new event in the organization
 *
 * @reason Allows admins to create new events
 */
export function useCreateEvent(): UseMutationResult<Event, unknown, CreateEventRequest, unknown> {
  return useMutation(async (eventData: CreateEventRequest) => {
    const response = await ApiV2.post<ApiResponse<Event>>('/api/events', eventData);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create event');
    }
    return response.data;
  });
}

/**
 * Updates event details
 *
 * @reason Allows admins to modify event information
 */
export function useUpdateEvent(
  eventId: string,
): UseMutationResult<void, unknown, UpdateEventRequest, unknown> {
  return useMutation(async (updates: UpdateEventRequest) => {
    if (!eventId) {
      throw new Error('Event ID is required');
    }
    const response = await ApiV2.put<ApiResponse<void>>(`/api/events/${eventId}`, updates);
    if (!response.success) {
      throw new Error(response.error || 'Failed to update event');
    }
  });
}

/**
 * Deletes an event
 *
 * @reason Allows admins to remove events
 */
export function useDeleteEvent(): UseMutationResult<void, unknown, string, unknown> {
  return useMutation(async (eventId: string) => {
    const response = await ApiV2.delete<ApiResponse<void>>(`/api/events/${eventId}`);
    if (!response.success) {
      throw new Error(response.error || 'Failed to delete event');
    }
  });
}

/**
 * Lists all members assigned to an event
 *
 * @reason Shows who has access to a specific event
 */
export function useListEventMembers(eventId?: string): UseQueryResult<EventMember[]> {
  return useQuery(
    [LIST_EVENT_MEMBERS_CACHE_KEY, eventId],
    async () => {
      if (!eventId) {
        return [];
      }
      const response = await ApiV2.get<ApiResponse<EventMember[]>>(
        `/api/events/${eventId}/members`,
      );
      return response.data || [];
    },
    {
      enabled: !!eventId,
    },
  );
}

/**
 * Assigns a user to an event
 *
 * @reason Allows admins to grant event access to users
 */
export function useAssignUserToEvent(
  eventId: string,
): UseMutationResult<EventMember, unknown, AssignMemberRequest, unknown> {
  return useMutation(async (memberData: AssignMemberRequest) => {
    const response = await ApiV2.post<ApiResponse<EventMember>>(
      `/api/events/${eventId}/members`,
      memberData,
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to assign user to event');
    }
    return response.data;
  });
}

/**
 * Updates a user's role for an event
 *
 * @reason Allows admins to change event access permissions
 */
export function useUpdateEventMember(
  eventId: string,
  email: string,
): UseMutationResult<void, unknown, { role: 'manager' | 'viewer' }, unknown> {
  return useMutation(async (data: { role: 'manager' | 'viewer' }) => {
    // URL encode the email since it contains special characters
    const encodedEmail = encodeURIComponent(email);
    const response = await ApiV2.patch<ApiResponse<void>>(
      `/api/events/${eventId}/members/${encodedEmail}`,
      data,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to update event member');
    }
  });
}

/**
 * Removes a user from an event
 *
 * @reason Allows admins to revoke event access
 */
export function useRemoveUserFromEvent(
  eventId: string,
): UseMutationResult<void, unknown, string, unknown> {
  return useMutation(async (email: string) => {
    // URL encode the email since it contains special characters
    const encodedEmail = encodeURIComponent(email);
    const response = await ApiV2.delete<ApiResponse<void>>(
      `/api/events/${eventId}/members/${encodedEmail}`,
    );
    if (!response.success) {
      throw new Error(response.error || 'Failed to remove user from event');
    }
  });
}
